/**
 * 設定 → 自社（発行者）情報（docs/16 #5 → 縦串6 docs/20 → #153、docs/62 §3・★1・★2、Figma「設定と自社情報（10/9）」④⑤⑥）。iOS のまとまり（docs/10 §9.15）。
 *   ・上の帯「‹ 設定｜自動で保存されます」（PC の2列は「‹ 設定」を出さず、題の右に）、大きな題、頭に会社のアイコン 72・会社名・業種（紙に出る名前とロゴを確かめられる）
 *   ・自動で保存（S-00008、10/10 射場さん「保存を押さずに振込口座を押すと消える。入力の流れで押すので自動保存に」）：
 *     選ぶ欄（会計年度・都道府県）と「住所検索」はすぐ、打つ欄は欄を離れたとき（Enter も）、画面を離れるとき（左の列・振込口座の行・戻る）は書きかけを送る。
 *     ページを閉じるときは keepalive で送る。題の右に「保存しています…」→「保存しました 10:31」。押しているあいだは送らない（画面がずれてボタンを外さない）。
 *     会社名が空なら送らず題の右に「会社名を入れると保存されます」、形の誤り（登録番号など。contract の CompanyUpdate で画面でも見る）・API に断られたら上の知らせ。
 *     どちらも直すまで送らない。そのあいだは「‹ 設定」と画面の中のリンク（左の列・左のメニュー・振込口座の行）で「入れた内容は消えます」を確かめる（S-00008 の続き）。
 *     しくみは lib/autoSave.tsx（見積・請求・見積依頼・言葉の言い換えも同じ）
 *   ・節：会社（会社名・代表者名・業種・会計年度の始まり）／社印とロゴ／住所と連絡先（郵便番号・電話・FAX は1つの欄＝★2）／インボイス（登録番号）／振込先（振込口座 ›）
 *   ・業種・社印・ロゴは選んだ瞬間に保存（今どおり。別の API）。住所・電話・代表者名は暗号化（ルール5）
 *   ・★1：発行者の情報だけ。前にここにあった9つ（取消し・改ページ・並べ方・〃・範囲の注記・積算表・一式の知らせ・粗利率・入金の上限）は
 *     「見積・請求」（/settings/estimate）へ移した。/settings/company#unissue（古いリンク）は /settings/estimate#unissue へ置き換える
 *   ・社印・ロゴの行を押すと選ぶだけのシート（見本・「画像選択」＝入力欄の <label>（Safari の決めごと 9/24）・「削除」・「取消」）。行に画像を落としても登録
 * 初期設定は聞かない（docs/16 #5）。必要になった人がここで変える
 */
import { COMPANY_MSG, CompanyUpdate, INDUSTRIES, INDUSTRY_LABEL, INDUSTRY_MSG, SETTINGS_MSG, SWITCHER_MSG, type BankAccountsPage, type Company, type ImageRef, type Industry } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { api, ApiError, apiUpload, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { AddressFields } from "../components/AddressFields.jsx";
import { BackButton, useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { CompanyIcon } from "../components/CompanyIcon.jsx";
import { GroupImageRow, GroupInputRow, GroupList, GroupNavRow, GroupSection, GroupSelectRow, ImageThumb, RowIcon } from "../components/GroupList.jsx";
import { PhoneField } from "../components/PhoneField.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, Help, guardComposingEnter, useToast } from "../components/ui.jsx";
import { AutoSaveNote, AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";
import { prepareImage } from "../lib/image.js";
import { useSettingsPane } from "../lib/settingsPane.js";
import { BANK_ACCOUNTS_KEY } from "./SettingsBankAccounts.jsx";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}月` }));

/** 「保存」で送る欄（★1：発行者の情報だけ。業種・社印・ロゴは選んだ瞬間に保存） */
type Form = Pick<Company, "name" | "representativeName" | "fiscalYearStartMonth" | "postalCode" | "address" | "building" | "phone" | "fax" | "invoiceRegistrationNo">;
const formOf = (c: Company): Form => ({
  name: c.name,
  representativeName: c.representativeName,
  fiscalYearStartMonth: c.fiscalYearStartMonth,
  postalCode: c.postalCode,
  address: c.address,
  building: c.building,
  phone: c.phone,
  fax: c.fax,
  invoiceRegistrationNo: c.invoiceRegistrationNo,
});

const hhmm = () => new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
/** 打つ欄にいるか（そのあいだは欄を離れるまで送らない。選ぶ欄・ボタンはすぐ送る） */
const typing = (): boolean => {
  const el = document.activeElement;
  return (el instanceof HTMLInputElement && el.type !== "file") || el instanceof HTMLTextAreaElement;
};

type ImageKind = "seal" | "logo";
const IMAGE_LABEL: Record<ImageKind, string> = { seal: SETTINGS_MSG.seal, logo: SETTINGS_MSG.logo };

export function SettingsCompanyPage() {
  const loc = useLocation();
  // 設定のメニューの前の行「見積・請求書の取消しと改ページ」（/settings/company#unissue）から来たら、移した先へ
  if (loc.hash === "#unissue") return <Navigate to="/settings/estimate#unissue" replace />;
  return <CompanyInfo />;
}

function CompanyInfo() {
  const qc = useQueryClient();
  const me = useMe();
  const pane = useSettingsPane();
  const { toast, node } = useToast();
  const formId = useId();
  const q = useQuery({ queryKey: ["company"], queryFn: () => api<Company>("GET", "/company") });
  const bank = useQuery({ queryKey: BANK_ACCOUNTS_KEY, queryFn: () => api<BankAccountsPage>("GET", "/bank-accounts") });
  // 自動で保存（S-00008。lib/autoSave.tsx）
  const a = useAutoSave<Form>({
    server: q.data ? formOf(q.data) : null,
    send: async (f) => {
      const c = await api<Company>("PUT", "/company", f satisfies CompanyUpdate);
      qc.setQueryData(["company"], c);
      void qc.invalidateQueries({ queryKey: ME_KEY }); // ヘッダーの会社名
      return formOf(c);
    },
    check: (f) => {
      // 会社名が空：上に知らせの行は足さない（画面がずれる）。題の右と欄の赤だけ
      if (!f.name.trim()) return { status: SETTINGS_MSG.autoSaveNeedName, note: SETTINGS_MSG.autoSaveNeedNameNote };
      const r = CompanyUpdate.safeParse(f);
      if (r.success) return null;
      const errors: Record<string, string> = {};
      for (const i of r.error.issues) {
        const k = String(i.path[0] ?? "");
        if (k && !errors[k]) errors[k] = i.message;
      }
      return { status: SETTINGS_MSG.autoSaveFailed, errors };
    },
    hide: (f) => ({ url: "/api/v1/company", method: "PUT", body: f }),
  });
  const form = a.form;
  const errors = a.errors;
  usePageName(SETTINGS_MSG.companyTitle);

  /** 業種（#142 段4）：選ぶとすぐ保存（PUT /company/industry、見本は入れない）。変えたら一文を添える（10/6 射場さん）＝#171 から通知の中（前は節の下の文） */
  const changeIndustry = async (v: Industry) => {
    try {
      const r = await api<{ message: string }>("PUT", "/company/industry", { industry: v, seed: false });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      toast(`${r.message.replace(/。$/, "")}。${INDUSTRY_MSG.changedNote}。`, undefined, "success", "industry-changed-note");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };

  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定。自動で保存するので確かめない（送れていない直しがあるときだけ確かめる。画面の中のリンクも）
  const back = useBackNav({ to: "/settings", label: "設定" }, a.unsent);
  const guard = useLeaveGuard(a.unsent);

  // ---- 社印・ロゴ（選んだ瞬間に登録。2026-09-24 射場さん「Safari で押しても反応しない」→ 押す所は入力欄の <label>） ----
  const [sheet, setSheet] = useState<ImageKind | null>(null);
  /** 閉じる動きのあいだも中身を描く（最後に開いた方） */
  const lastSheet = useRef<ImageKind>("seal");
  if (sheet) lastSheet.current = sheet;
  const [busy, setBusy] = useState<ImageKind | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const onImage = (c: Company) => qc.setQueryData(["company"], (old: Company | undefined) => (old ? { ...old, seal: c.seal, logo: c.logo } : c));
  /** 決まり（PNG・JPEG、2MB・1500px）に合わない画像は、この端末で縮めて PNG にしてから送る（lib/image.ts） */
  const upload = async (kind: ImageKind, f: File | undefined) => {
    if (!f || busy) return;
    setBusy(kind);
    try {
      const img = await prepareImage(f);
      if (!img) {
        toast(COMPANY_MSG.imageUnreadable);
        return;
      }
      onImage(await apiUpload<Company>(`/company/${kind}`, img.blob, img.contentType));
      setSheet(null);
      if (img.resized) toast(SETTINGS_MSG.imageResized.replace("{w}", String(img.width)).replace("{h}", String(img.height)));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const removeImage = async (kind: ImageKind) => {
    setBusy(kind);
    try {
      onImage(await api<Company>("DELETE", `/company/${kind}`));
      setSheet(null);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(null);
    }
  };

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!form || !q.data || !me.data) return <p className="hint">読み込んでいます…</p>;
  const c = q.data;
  const m = me.data;
  const admin = m.user.role === "admin";
  const set = (patch: Partial<Form>) => a.set(patch);
  const firstError = Object.values(errors)[0];
  const industry = m.tenant.industry;
  const defaultBank = bank.data?.accounts.find((a) => a.isDefault);
  const image = (kind: ImageKind): ImageRef | null => (kind === "seal" ? c.seal : c.logo);
  const kind = sheet ?? lastSheet.current;
  const cur = image(kind);

  // 前の「保存」の場所に、保存の様子（S-00008）。短い定型句だけで、説明は題の下の1行（S-00009）
  const saveBtn = <AutoSaveStatus state={a.state} testId="company-autosave" />;

  return (
    <div className={"ppage cpage" + (pane.twoCol ? " is-pane" : "")} data-testid="company">
      {pane.twoCol ? null : (
        <div className="cotop">
          <BackButton back={back} />
          <span className="cotop-sp" />
          {saveBtn}
        </div>
      )}
      {back.dialog}
      {guard.dialog}
      <div className="ptitle-row">
        <h1 className="ptitle">{SETTINGS_MSG.companyTitle}</h1>
        {pane.twoCol ? saveBtn : null}
      </div>
      <AutoSaveNote state={a.state} testId="company-autosave-note" />

      <div className="chead" data-testid="company-head">
        <CompanyIcon name={c.name} logoUrl={c.logo?.url} size={72} />
        <span className="chead-tx">
          <b className={"chead-name" + (c.name ? "" : " is-none")} data-testid="company-head-name">
            {c.name || SWITCHER_MSG.noName}
          </b>
          {industry ? <span className="chead-ind">{INDUSTRY_LABEL[industry]}</span> : null}
        </span>
      </div>
      <ErrorText>{firstError ?? a.serverError}</ErrorText>

      <form
        id={formId}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void a.flush();
        }}
        {...a.formProps}
      >
        <GroupSection
          title={SETTINGS_MSG.sectionCompany}
          help={<Help testId="help-company" text="見積書・請求書の右上に印字されます。住所・電話・代表者名は暗号化して保存します。会計年度の始まりの月は「今期の数字」を数える始まりの月です。4月始まりなら「4月〜翌年3月」が今期。" />}
          note={SETTINGS_MSG.representativeNote}
          data-testid="company-sec-company"
        >
          <GroupList>
            <GroupInputRow id="st_name" label={SETTINGS_MSG.name} wide>
              <input id="st_name" className="giinp" autoComplete="organization" maxLength={100} value={form.name} aria-invalid={!!errors.name || (a.state.kind === "blocked" && !form.name.trim()) || undefined} onKeyDown={guardComposingEnter} onChange={(e) => set({ name: e.target.value })} />
            </GroupInputRow>
            <GroupInputRow id="st_rep" label={SETTINGS_MSG.representative} wide>
              <input id="st_rep" className="giinp" maxLength={60} value={form.representativeName} onKeyDown={guardComposingEnter} onChange={(e) => set({ representativeName: e.target.value })} />
            </GroupInputRow>
            <GroupSelectRow
              id="st_industry"
              label={INDUSTRY_MSG.label}
              value={industry ?? ""}
              placeholder={industry ? undefined : "—"}
              options={INDUSTRIES[m.tenant.product].map((k) => ({ value: k, label: INDUSTRY_LABEL[k] }))}
              disabled={!admin}
              onChange={(v) => v && void changeIndustry(v as Industry)}
              selectTestId="industry-select"
            />
            <GroupSelectRow id="st_fy" label={SETTINGS_MSG.fiscalYear} value={String(form.fiscalYearStartMonth)} options={MONTH_OPTIONS} onChange={(v) => set({ fiscalYearStartMonth: Number(v) })} />
          </GroupList>
        </GroupSection>

        <GroupSection
          title={SETTINGS_MSG.sectionImages}
          help={<Help testId="help-images" text="登録すると見積書・請求書・領収書に印字します（社印は発行者欄に重ねて、ロゴはその上に）。PNG・JPEG などの画像を選ぶか、行にドロップしてください。大きい画像は縮めて登録します。社印は背景が透明な PNG がきれいです。ロゴは横長の画像が収まりやすいです（帳票の上では高さ 12mm）。" />}
          data-testid="company-sec-images"
        >
          <GroupList>
            {(["seal", "logo"] as const).map((kind) => (
              <GroupImageRow
                key={kind}
                label={IMAGE_LABEL[kind]}
                url={image(kind)?.url}
                value={image(kind) ? SETTINGS_MSG.imageChange : SETTINGS_MSG.imageNone}
                busy={busy === kind}
                onClick={() => setSheet(kind)}
                onDropFile={(f) => void upload(kind, f)}
                testId={`image-${kind}`}
                buttonTestId={`image-${kind}-open`}
              />
            ))}
          </GroupList>
        </GroupSection>

        <GroupSection title={SETTINGS_MSG.sectionAddress} data-testid="company-sec-address">
          <GroupList>
            <AddressFields single value={{ postalCode: form.postalCode, address: form.address, building: form.building }} onChange={(v) => set(v)} ids={{ zip: "st_zip", address: "st_addr", building: "st_bldg" }} />
            <GroupInputRow id="st_tel" label="電話" wide>
              <PhoneField id="st_tel" value={form.phone} autoComplete="tel" invalid={!!errors.phone} onChange={(phone) => set({ phone })} />
            </GroupInputRow>
            <GroupInputRow id="st_fax" label="FAX" wide>
              <PhoneField id="st_fax" value={form.fax} invalid={!!errors.fax} onChange={(fax) => set({ fax })} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>

        <GroupSection title={SETTINGS_MSG.sectionInvoice} note={SETTINGS_MSG.regNoNote} data-testid="company-sec-invoice">
          <GroupList>
            <GroupInputRow id="st_reg" label={SETTINGS_MSG.regNo} wide>
              <input id="st_reg" className="giinp" value={form.invoiceRegistrationNo} aria-invalid={!!errors.invoiceRegistrationNo || undefined} onKeyDown={guardComposingEnter} onChange={(e) => set({ invoiceRegistrationNo: e.target.value })} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
      </form>

      <GroupSection title={SETTINGS_MSG.sectionBank} data-testid="company-sec-bank">
        <GroupList>
          <GroupNavRow
            icon={<RowIcon k="bank" />}
            title={SETTINGS_MSG.bank}
            value={defaultBank ? `${defaultBank.bankName} ${defaultBank.branchName}`.trim() : bank.data ? SETTINGS_MSG.bankNone : undefined}
            valueTestId="company-bank-value"
            to="/settings/bank-accounts"
            buttonId="toBankAccounts"
          />
        </GroupList>
      </GroupSection>

      <Sheet open={sheet !== null} actions title={IMAGE_LABEL[kind]} cancelLabel={SETTINGS_MSG.imageCancel} onCancel={() => setSheet(null)} testId="image-sheet" cancelTestId="image-sheet-cancel">
        <div className="imgsheet-head">
          <ImageThumb url={cur?.url} alt={IMAGE_LABEL[kind]} size={88} />
          <p className="imgsheet-cap">
            {IMAGE_LABEL[kind]}
            {cur ? <span data-testid="image-size">{`　${cur.width}×${cur.height}px`}</span> : null}
          </p>
        </div>
        <input ref={fileRef} id={`st_${kind}`} className="file-hidden" type="file" accept="image/*" disabled={busy !== null} onChange={(e) => void upload(kind, e.target.files?.[0])} />
        <label htmlFor={`st_${kind}`} className={"gsheet-act filebtn" + (busy ? " is-busy" : "")} aria-disabled={busy !== null || undefined} data-testid="image-pick">
          {busy === kind ? SETTINGS_MSG.imageBusy : SETTINGS_MSG.imagePick}
        </label>
        {cur ? (
          <button type="button" className="gsheet-act is-danger" disabled={busy !== null} onClick={() => void removeImage(kind)} data-testid="image-remove">
            {SETTINGS_MSG.imageRemove}
          </button>
        ) : null}
      </Sheet>
      {node}
    </div>
  );
}
